import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { notify } from '@/utils/notify';
import { loadPdfFontBase64, PDF_FONT_MAX_MB } from '@/utils/pdfFontLoader';
import { drawTiledImageWatermark } from '@/utils/pdfWatermarkTiler';

function addWatermark(doc, text) {
    try {
        const w = doc.internal.pageSize.width;
        const h = doc.internal.pageSize.height;
        doc.setFontSize(48);
        doc.setTextColor(220, 220, 220);
        try { doc.setGState && doc.setGState(new doc.GState({ opacity: 0.12 })); } catch (e) { }
        doc.text(text, w / 2, h / 2, { align: 'center', angle: 45 });
        doc.setTextColor(0);
    } catch (e) {
        // ignore
    }
}

function buildColumnStylesByPercent(doc, left, right, percentages) {
    const pageW = doc.internal.pageSize.width;
    const tableW = pageW - left - right;
    const total = (percentages || []).reduce((sum, p) => sum + (Number(p) || 0), 0) || 1;

    return percentages.reduce((acc, p, idx) => {
        const w = Number(((tableW * (Number(p) || 0)) / total).toFixed(2));
        acc[idx] = { cellWidth: w, minCellWidth: w };
        return acc;
    }, {});
}

export async function exportCompanyOverviewPdf({
    api,
    store,
    comBriefVisible,
    basicInfo,
    businessStatus,
    contribution,
    creditStatus,
    fallbackCompanyName = ''
}) {
    if (!comBriefVisible) {
        notify({ title: '錯誤', message: '目前無資料可匯出, 請先查詢公司資料', type: 'error' });
        return;
    }

    try {
        store && store.dispatch && store.dispatch('loadingHandler', true);

        const fontB64 = await loadPdfFontBase64();

        if (!fontB64) {
            notify({ title: '錯誤', message: `無法載入中文字型，請確認 /fonts/NotoSansTC-Regular.ttf 可存取且大小不超過${PDF_FONT_MAX_MB}MB`, type: 'error' });
            store && store.dispatch && store.dispatch('loadingHandler', false);
            return;
        }

        const doc = new jsPDF('p', 'mm', 'a4');
        doc.addFileToVFS('NotoSansTC-Regular.ttf', fontB64);
        doc.addFont('NotoSansTC-Regular.ttf', 'NotoSansTC', 'normal');
        try { doc.addFont('NotoSansTC-Regular.ttf', 'NotoSansTC', 'bold'); } catch (e) { }
        doc.setFont('NotoSansTC', 'normal');
        let justAddedPage = false;

        let watermarkText = '機密資料';
        let watermarkImage = null;
        try {
            if (api && api.watermark && api.watermark.get_watermark) {
                const wmRes = await api.watermark.get_watermark({});
                if (wmRes) {
                    if (wmRes.watermark) watermarkText = wmRes.watermark;
                    else if (wmRes.data && wmRes.data.watermark) watermarkText = wmRes.data.watermark;
                    if (wmRes.data && wmRes.data.image) watermarkImage = wmRes.data.image;
                }
            }
        } catch (e) {
            // ignore
        }

        const companyName = (basicInfo && basicInfo.CUS_NAME) ? String(basicInfo.CUS_NAME) : fallbackCompanyName;

        doc.setFontSize(13);
        doc.text(companyName, 14, 20);
        doc.setFontSize(10);
        doc.text('一、客戶基本資料', 14, 28);

        const basicRows = [
            ['公司名稱', basicInfo.CUS_NAME || ''],
            ['公司英文名稱', basicInfo.CUS_ENG_NAME || ''],
            ['公司統一編號', basicInfo.CUS_IDN || ''],
            ['公司設立日', basicInfo.CUS_BIRTHDAY || ''],
            ['國別', basicInfo.CUS_COUNTRY_CODE || ''],
            ['負責人', basicInfo.CUS_REP_NAME || ''],
            ['行業別', basicInfo.CUS_IDU_NAME || ''],
            ['組織型態', basicInfo.CUS_ORG_TYPE_NAME || ''],
            ['客戶等級', basicInfo.CUS_GRADE || ''],
            ['利害關係人', basicInfo.INTEREST_REL || ''],
            ['債務協商', basicInfo.NOTE_L5 || '']
        ];

        const chunkedRows = [];
        for (let i = 0; i < basicRows.length; i += 3) {
            const row = [];
            for (let j = 0; j < 3; j++) {
                const pair = basicRows[i + j];
                if (pair) {
                    row.push(pair[0]);
                    row.push(pair[1]);
                } else {
                    row.push('');
                    row.push('');
                }
            }
            chunkedRows.push(row);
        }

        console.log("basicRows",basicRows)
        let biz_info_visible = false;
        Object.keys(basicRows).forEach(key => {
            const label = basicRows[key][0]; // 例如：'公司名稱'
            const value = basicRows[key][1]; // 實際的值

            // 印出目前檢查的狀態（可留著 Debug，確認都有掃描到）
            console.log(`檢查欄位 [${label}]:`, value);

            // 修正：增加 .trim() 避免使用者輸入一堆「太空字元」也被誤判成有資料
            // 同時確保 null 或 undefined 在轉型字串後不會漏判
            if (value !== null && value !== undefined && String(value).trim() !== '') {
                console.log("👉 進入 true！發現有資料的欄位:", label, "，值為:", value);
                biz_info_visible = true;
            }
        });    
        console.log("biz_info_visible",biz_info_visible)   

        if( biz_info_visible){ // 有資料, 寫基本資料.
            autoTable(doc, {
                startY: 34,
                body: chunkedRows,
                theme: 'plain',
                styles: { font: 'NotoSansTC', fontSize: 8, cellPadding: 1.5, valign: 'middle' },
                columnStyles: { 0: { cellWidth: 24 }, 1: { cellWidth: 37 }, 2: { cellWidth: 24 }, 3: { cellWidth: 37 }, 4: { cellWidth: 24 }, 5: { cellWidth: 36 } },
                margin: { left: 14, right: 14 },
                didParseCell: function(data) {
                    if (data.section === 'body') {
                        const ci = data.column.index;
                        if (ci === 0 || ci === 2 || ci === 4) {
                            data.cell.styles.fillColor = [245,210,162];
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                }
            });
        }
        else { // 如果沒有資料, 寫出"暫無數據"
            console.log("寫出無數據!")
            autoTable(doc, {
                startY: 34,
                margin: { left: 14, right: 14 },
                body: [
                    [
                        { 
                            content: '暫無數據', 
                            colSpan: 1, // 單一格子即可，會吃滿設定的寬度
                            styles: { 
                                halign: 'center',        // 2. 讓文字水平置中在頁面正中間
                                textColor: [150, 150, 150], // 淺灰色
                                fontSize: 8 
                            } 
                        }
                    ]
                ],
                theme: 'striped', // 或使用 'grid'，使其帶有微幅底色或外框
                styles: { font: 'NotoSansTC', cellPadding: 4, valign: 'middle' }
            });
        }


        let section2TitleY = 36;
        {
            const pageH = doc.internal.pageSize.height;
            const topY = 36;
            const prevFinalY = (!justAddedPage && doc.lastAutoTable && typeof doc.lastAutoTable.finalY === 'number') ? doc.lastAutoTable.finalY : topY;
            section2TitleY = prevFinalY + 12;
            if (section2TitleY > pageH - 36) { doc.addPage(); justAddedPage = true; section2TitleY = topY; }
            doc.setFontSize(10);
            doc.text('二、營業狀況', 14, section2TitleY);

            doc.setFontSize(9);
            doc.text('個體營業狀況', 14, section2TitleY + 7);
            doc.text('單位:新臺幣千元', doc.internal.pageSize.width - 14, section2TitleY + 7, { align: 'right' });
        }
        const indCols = [
            { header: '年度', dataKey: 'DATE' },
            { header: '營業收入', dataKey: 'SD1' },
            { header: '成長率', dataKey: 'SD2' },
            { header: '營業毛利', dataKey: 'SD3' },
            { header: '營業利益', dataKey: 'SD4' },
            { header: '營業外收支淨額', dataKey: 'SD7' },
            { header: '稅前損益', dataKey: 'SD8' },
            { header: '稅前損益率', dataKey: 'SD9' }
        ];
        // const business8ColStyles = buildColumnStylesByPercent(doc, 14, 14, [1, 1, 1, 1, 1, 1, 1, 1]);
        const business8ColStyles = buildColumnStylesByPercent(doc, 14, 14, [1, 1, 1, 1, 1, 1, 1, 1]);

        // 除了第 0 欄(年度/日期)靠左外，第 1 欄以後全部自動加上靠右對齊
        Object.keys(business8ColStyles).forEach(colIndex => {
            if (colIndex !== '0') {
                business8ColStyles[colIndex].halign = 'right';
            }
        });        

        let indRows = (businessStatus.individual || []).map(r => ({
            DATE: r.DATE_IFRS || '',
            SD1: r.SD1_IFRS || '',
            SD2: (r.SD2_IFRS != null && r.SD2_IFRS !== '') ? (String(r.SD2_IFRS).endsWith('%') ? r.SD2_IFRS : r.SD2_IFRS + '%') : '',
            SD3: r.SD3_IFRS || '',
            SD4: r.SD4_IFRS || '',
            SD7: r.SD9_IFRS || '',
            SD8: r.SD7_IFRS || '',
            SD9: (r.SD8_IFRS != null && r.SD8_IFRS !== '') ? (String(r.SD8_IFRS).endsWith('%') ? r.SD8_IFRS : r.SD8_IFRS + '%') : ''
        }));

        // === 新增以下判斷：若無資料，顯示「暫無數據」 ===
        if (indRows.length === 0) {
            indRows = [{
                DATE: { 
                    content: '暫無數據', 
                    colSpan: 8, 
                    styles: { halign: 'center', textColor: [150, 150, 150], fontSize: 8 } 
                }
            }];
        }


        const indPrevFinalYMain = (!justAddedPage && doc.lastAutoTable && typeof doc.lastAutoTable.finalY === 'number') ? doc.lastAutoTable.finalY : 36;
        const indTableStart = Math.max(indPrevFinalYMain + 7, (section2TitleY || 36) + 11, 44);
        justAddedPage = false;
        autoTable(doc, {
            startY: indTableStart,
            columns: indCols,
            body: indRows,
            styles: { font: 'NotoSansTC', fontSize: 7, cellPadding: 1, valign: 'middle' },
            headStyles: { font: 'NotoSansTC', fontStyle: 'bold', fillColor: [245,210,162], textColor: 60, halign: 'center' }, 
            alternateRowStyles: { fillColor: [210,210,210] },
            margin: { left: 14, right: 14 }, columnStyles: business8ColStyles, showHead: 'everyPage'
        });

        let descFinalY = (!justAddedPage && doc.lastAutoTable && typeof doc.lastAutoTable.finalY === 'number') ? doc.lastAutoTable.finalY : 44;
        {
            const pageHDesc = doc.internal.pageSize.height;
            let descY = descFinalY + 6;
            if (descY > pageHDesc - 24) {
                doc.addPage();
                justAddedPage = true;
                descY = 36;
            }
            const descText = businessStatus.desc_IFRS || '';
            const descLines = descText ? doc.splitTextToSize(descText, doc.internal.pageSize.width - 28) : [''];
            doc.setFontSize(8);
            doc.text(descLines, 14, descY);
            descFinalY = descY + Math.max(0, (descLines.length - 1) * 4);
        }

        const ifrsCols = [
            { header: '年度', dataKey: 'DATE_IFRS' },
            { header: '營業收入', dataKey: 'SD1_IFRS' },
            { header: '成長率', dataKey: 'SD2_IFRS' },
            { header: '營業毛利', dataKey: 'SD3_IFRS' },
            { header: '營業利益', dataKey: 'SD4_IFRS' },
            { header: '營業外收支淨額', dataKey: 'SD7_IFRS' },
            { header: '稅前損益', dataKey: 'SD8_IFRS' },
            { header: '稅前損益率', dataKey: 'SD9_IFRS' }
        ];
        let ifrsRows = (businessStatus.IFRS || []).map(r => ({
            DATE_IFRS: r.DATE_IFRS || r.DATE || '',
            SD1_IFRS: r.SD1_IFRS || '',
            SD2_IFRS: (r.SD2_IFRS != null && r.SD2_IFRS !== '') ? (String(r.SD2_IFRS).endsWith('%') ? r.SD2_IFRS : r.SD2_IFRS + '%') : '',
            SD3_IFRS: r.SD3_IFRS || '',
            SD4_IFRS: r.SD4_IFRS || '',
            SD7_IFRS: r.SD9_IFRS || '',
            SD8_IFRS: r.SD7_IFRS || '',
            SD9_IFRS: (r.SD8_IFRS != null && r.SD8_IFRS !== '') ? (String(r.SD8_IFRS).endsWith('%') ? r.SD8_IFRS : r.SD8_IFRS + '%') : ''
        }));

        // === 新增以下判斷：若無資料，顯示「暫無數據」 ===
        if (ifrsRows.length === 0) {
            ifrsRows = [{
                DATE_IFRS: { 
                    content: '暫無數據', 
                    colSpan: 8, 
                    styles: { halign: 'center', textColor: [150, 150, 150], fontSize: 8 } 
                }
            }];
        }

        {
            const pageHIfs = doc.internal.pageSize.height;
            const prevFinalYIfrsMain = (!justAddedPage && doc.lastAutoTable && typeof doc.lastAutoTable.finalY === 'number') ? doc.lastAutoTable.finalY : 44;
            let titleYIfrs = Math.max(prevFinalYIfrsMain + 12, descFinalY + 8);
            if (titleYIfrs > pageHIfs - 36) { doc.addPage(); justAddedPage = true; titleYIfrs = 36; }
            doc.setFontSize(9);
            doc.text('合併營業狀況(IFRS)', 14, titleYIfrs);
            doc.text('單位:新臺幣千元', doc.internal.pageSize.width - 14, titleYIfrs, { align: 'right' });

            const ifrsTableStart = Math.max(prevFinalYIfrsMain + 7, titleYIfrs + 7);
            autoTable(doc, {
                startY: ifrsTableStart,
                columns: ifrsCols,
                body: ifrsRows,
                styles: { font: 'NotoSansTC', fontSize: 7, cellPadding: 1, valign: 'middle' },
                headStyles: { font: 'NotoSansTC', fontStyle: 'bold', fillColor: [245,210,162], textColor: 60, halign: 'center' },
                alternateRowStyles: { fillColor: [210,210,210] },
                margin: { left: 14, right: 14 },
                columnStyles: business8ColStyles,
                showHead: 'everyPage'
            });
        }
        justAddedPage = false;

        const contributionPeriod = contribution.period || '';

        let section3TitleY = 36;
        {
            const pageHContrib = doc.internal.pageSize.height;
            const prevFinalYContribMain = (!justAddedPage && doc.lastAutoTable && typeof doc.lastAutoTable.finalY === 'number') ? doc.lastAutoTable.finalY : 36;
            section3TitleY = prevFinalYContribMain + 8;
            if (section3TitleY > pageHContrib - 36) { doc.addPage(); justAddedPage = true; section3TitleY = 36; }
            doc.setFontSize(10);
            doc.text('三、主要業務貢獻度', 14, section3TitleY);
            const periodLineY = section3TitleY + 6;
            doc.setFontSize(9);
            doc.text(`統計起訖日：${contributionPeriod}`, 14, periodLineY);
            doc.text('單位:新臺幣元', doc.internal.pageSize.width - 14, periodLineY, { align: 'right'});

            // 1. 定義文字內容
            const textStr = '單位:新臺幣元';

            // 2. 計算 X 軸起點（頁面寬度 - 右邊界 14）
            const textX = doc.internal.pageSize.width - 14; 

            // 3. 繪製靠右對齊的文字
            doc.text(textStr, textX, periodLineY, { align: 'right' });

            // 4. 計算文字的實際寬度 (將 jsPDF 的內部單位轉為 mm)
            const textWidth = (doc.getStringUnitWidth(textStr) * 9) / doc.internal.scaleFactor;

            // 5. 畫底線 (X 軸從「最右邊減掉文字寬度」畫到「最右邊」)
            doc.setLineWidth(0.2); // 線條粗細
            const lineY = periodLineY + 1; // 文字下方 1mm 的位置
            doc.line(textX - textWidth, lineY, textX, lineY);                    
        }

        const contribCols = [
            { header: '業務群組-產品別', dataKey: 'BUS_GROUP_LABEL_DESC' },
            { header: '平均餘額／交易', dataKey: 'PROF_AVE_BALANCE' },
            { header: '資金運用收入', dataKey: 'PROF_FUND_INCOME' },
            { header: '資金運用成本', dataKey: 'PROF_FUND_COST' },
            { header: '資金運用收益', dataKey: 'PROF_FUND_PROF' },
            { header: '手續費收益', dataKey: 'PROF_CMS_PROF' },
            { header: '其他收入', dataKey: 'PROF_OTHER_INCOME' },
            { header: '兌換收益', dataKey: 'PROF_GA_LOS_XC' },
            { header: '貢獻度', dataKey: 'PROF_CLIENT_PROF' }
        ];
        const contribColumnStyles = buildColumnStylesByPercent(doc, 14, 14, [1.8, 1.2, 1, 1, 1, 1, 0.8, 0.6, 1]);
        // 除了第 0 欄(業務群組-產品別)靠左外，第 1 欄以後全部自動加上靠右對齊
        Object.keys(contribColumnStyles).forEach(colIndex => {
        if (colIndex !== '0') {
            contribColumnStyles[colIndex].halign = 'right';
        }
        });

        let contribRows = (contribution.detail || []).map(r => ({
            BUS_GROUP_LABEL_DESC: r.BUS_GROUP_LABEL_DESC || '',
            PROF_AVE_BALANCE: r.PROF_AVE_BALANCE || '',
            PROF_FUND_INCOME: r.PROF_FUND_INCOME || '',
            PROF_FUND_COST: r.PROF_FUND_COST || '',
            PROF_FUND_PROF: r.PROF_FUND_PROF || '',
            PROF_CMS_PROF: r.PROF_CMS_PROF || '',
            PROF_OTHER_INCOME: r.PROF_OTHER_INCOME || '',
            PROF_GA_LOS_XC: r.PROF_GA_LOS_XC || '',
            PROF_CLIENT_PROF: r.PROF_CLIENT_PROF || ''
        }));
        // === 新增以下判斷：若無資料，顯示「暫無數據」 ===
        if (contribRows.length === 0) {
            contribRows = [{
                BUS_GROUP_LABEL_DESC: { 
                    content: '暫無數據', 
                    colSpan: 9, 
                    styles: { halign: 'center', textColor: [150, 150, 150], fontSize: 8 } 
                }
            }];
        }        
        const contribPrevFinalYMain = (!justAddedPage && doc.lastAutoTable && typeof doc.lastAutoTable.finalY === 'number') ? doc.lastAutoTable.finalY + 10 : 45;
        const contribTableStart = Math.max(contribPrevFinalYMain + 7, (section3TitleY || 36) + 7, 36);
        justAddedPage = false;
        autoTable(doc, {
            startY: contribTableStart,
            columns: contribCols,
            body: contribRows,
            styles: { font: 'NotoSansTC', fontSize: 7, cellPadding: 1, valign: 'middle' },
            headStyles: { font: 'NotoSansTC', fontStyle: 'bold', fillColor: [245,210,162], textColor: 60, halign: 'center' },
            alternateRowStyles: { fillColor: [210,210,210] },
            margin: { left: 14, right: 14 },
            columnStyles: contribColumnStyles,
            showHead: 'everyPage'
        });

        const stats = contribution.statistics || {};
        const statsRows = [
            ['貢獻度合計', stats.ROA_CLIENT_PROF || ''],
            ['放款收益', stats.ROA_FUND_PROF_LOAN || ''],
            ['保證手續費收益', stats.ROA_1886 || ''],
            ['一般手續費收益', stats.ROA_NON1886 || ''],
            ['存款收益', stats.ROA_FUND_PROF_SAVING || ''],
            ['信用卡業務收益', stats.ROA_FUND_PROF_IC || ''],
            ['融資交易資金運用收益', stats.ROA_FUND_PROF_SMS || ''],
            ['ROA1', stats.ROA1 || ''],
            ['ROA2', stats.ROA2 || ''],
            ['ROA3', stats.ROA3 || '']
        ];

        const prevFinalYStatsMain = (!justAddedPage && doc.lastAutoTable && typeof doc.lastAutoTable.finalY === 'number') ? doc.lastAutoTable.finalY : 120;
        const statsStartY = Math.max(prevFinalYStatsMain + 8, 120);
        justAddedPage = false;
        doc.setFontSize(7);
        doc.text('註：本表相關資料僅列示至單位元，倘有完整數據需求，以CRM3系統為準', 14, statsStartY-5);        
        doc.setFontSize(10);
        doc.text('主要業務貢獻度統計', 14, statsStartY + 3);

        const chunkSize = 3;
        const tableRows = [];
        
        for (let i = 0; i < statsRows.length; i += chunkSize) {
            const chunk = statsRows.slice(i, i + chunkSize);
            const row = [];
            for (let k = 0; k < chunkSize; k++) {                
                const it = chunk[k] || ['', ''];
                row.push(it[0], it[1]);
            }
            tableRows.push(row);
        }

        let contribution_statistics_visible = false;
        Object.keys(statsRows).forEach(key => {
            const label = statsRows[key][0]; // 例如：'公司名稱'
            const value = statsRows[key][1]; // 實際的值

            // 印出目前檢查的狀態（可留著 Debug，確認都有掃描到）
            console.log(`檢查欄位 [${label}]:`, value);            

            // 修正：增加 .trim() 避免使用者輸入一堆「太空字元」也被誤判成有資料
            // 同時確保 null 或 undefined 在轉型字串後不會漏判
            if (value !== null && value !== undefined && String(value).trim() !== '') {
                console.log("👉 進入 true！發現有資料的欄位:", label, "，值為:", value);
                contribution_statistics_visible = true;
            }

        });
        console.log("是否有統計值", contribution_statistics_visible)

        const statsTableStart = statsStartY + 7;
        // 如果有資料, 則用原來的寫法.
        if( contribution_statistics_visible) {
            autoTable(doc, {
                startY: statsTableStart,
                body: tableRows,
                theme: 'plain',
                styles: { font: 'NotoSansTC', fontSize: 8, cellPadding: 1.5, valign: 'middle' },
                columnStyles: { 0: { cellWidth: 28 }, 1: { cellWidth: 33 }, 2: { cellWidth: 28 }, 3: { cellWidth: 33 }, 4: { cellWidth: 28 }, 5: { cellWidth: 32 } },
                margin: { left: 14, right: 14 },
                didParseCell: function(data) {
                    if (data.section === 'body') {
                        const colIndex = data.column.index;
                        if (colIndex === 0 || colIndex === 2 || colIndex === 4) {
                            data.cell.styles.fillColor = [245,210,162];
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                }
            });
        }
        else { // 如果沒有資料, 寫出"暫無數據"
            console.log("寫出無數據!")
            autoTable(doc, {
                startY: statsTableStart,
                margin: { left: 14, right: 14 },
                body: [
                    [
                        { 
                            content: '暫無數據', 
                            colSpan: 1, // 單一格子即可，會吃滿設定的寬度
                            styles: { 
                                halign: 'center',        // 2. 讓文字水平置中在頁面正中間
                                textColor: [150, 150, 150], // 淺灰色
                                fontSize: 8 
                            } 
                        }
                    ]
                ],
                theme: 'striped', // 或使用 'grid'，使其帶有微幅底色或外框
                styles: { font: 'NotoSansTC', cellPadding: 4, valign: 'middle' }
            });
        }


        let sectionTitleYMain = 36;
        {
            const pageHMain = doc.internal.pageSize.height;
            const prevFinalYMainSection = (!justAddedPage && doc.lastAutoTable && typeof doc.lastAutoTable.finalY === 'number') ? doc.lastAutoTable.finalY : 36;
            sectionTitleYMain = prevFinalYMainSection + 12;
            if (sectionTitleYMain > pageHMain - 36) { doc.addPage(); justAddedPage = true; sectionTitleYMain = 36; }
            doc.setFontSize(10);
            doc.text('四、授信往來情形', 14, sectionTitleYMain);
            doc.text('單位:新臺幣千元', doc.internal.pageSize.width - 14, sectionTitleYMain, { align: 'right' });
        }

        doc.setFontSize(10);
        //console.log('主要授信往來', creditStatus.main);
        const mainList = Array.isArray(creditStatus.main) ? creditStatus.main : [];
        const mainFirst = mainList[0] || {};  

        // const cusTitle = `${String(mainFirst.CUS_NAME || '').trim()} (${mainFirst.CUSIDN || ''})`;
        const cusName = String(mainFirst.CUS_NAME || '').trim();
        // 先用 || '' 擋掉 null/undefined，再轉型與 trim
        const idnTrimmed = String(mainFirst.CUSIDN || '').trim();
        const cusIdn = idnTrimmed ? ` (${idnTrimmed})` : '';
        const cusTitle = `${cusName}${cusIdn}`;    

        const pageHMain = doc.internal.pageSize.height;
        let startY = (!justAddedPage && doc.lastAutoTable && typeof doc.lastAutoTable.finalY === 'number') ? doc.lastAutoTable.finalY + 10 : 36;
        startY = Math.max(startY, (sectionTitleYMain || 36) + 8);
        if (startY > pageHMain - 40) { doc.addPage(); justAddedPage = true; startY = 36; }
        doc.text(cusTitle, 12, startY -2);

        const mainCols = [
            { header: '分行別', dataKey: 'CUS_BRANCH' },
            { header: '授信項目', dataKey: 'CREDIT_ITEM' },
            { header: '訂約金額', dataKey: 'CONTRACT_AMOUNT' },
            { header: '授信餘額', dataKey: 'CREDIT_BAL' },
            { header: '貸款期間', dataKey: 'LOAN_TERM' },
            { header: '擔保品', dataKey: 'COLLATERAL' },
            { header: '備註', dataKey: 'REMARKS' }
        ];
        const creditColPercentages = [6, 20, 15, 15, 20, 15, 6];
        // 第2, 3欄位, 需要靠右排

        const creditColumnStyles = buildColumnStylesByPercent(doc, 14, 14, creditColPercentages);

        Object.keys(creditColumnStyles).forEach(colIndex => {
            if (colIndex === '2' || colIndex === '3') {
                creditColumnStyles[colIndex].halign = 'right';
            }
            // 第 4 欄位 (貸款期間) 置中對齊
            else if (colIndex === '4') {
                creditColumnStyles[colIndex].halign = 'center';
            }
        });

        let mainRows = (creditStatus.main || []).map(r => ({ CUS_BRANCH: r.CUS_BRANCH || '', CREDIT_ITEM: r.CREDIT_ITEM || '', CONTRACT_AMOUNT: r.CONTRACT_AMOUNT || '', CREDIT_BAL: r.CREDIT_BAL || '', LOAN_TERM: r.LOAN_TERM || '', COLLATERAL: r.COLLATERAL || '', REMARKS: r.REMARKS || '' }));

        // === 新增以下判斷：若無資料，顯示「暫無數據」 ===
        if (mainRows.length === 0) {
            mainRows = [{
                CUS_BRANCH: { 
                    content: '暫無數據', 
                    colSpan: 7, 
                    styles: { halign: 'center', textColor: [150, 150, 150], fontSize: 8 } 
                }
            }];
        }        

        const mainPrevFinalYSection = (!justAddedPage && doc.lastAutoTable && typeof doc.lastAutoTable.finalY === 'number') ? doc.lastAutoTable.finalY + 10 : 45;
        const mainTableStart = Math.max(mainPrevFinalYSection + 8, sectionTitleYMain + 8, 36);
        justAddedPage = false;
        autoTable(doc, {
            startY: mainTableStart,
            columns: mainCols,
            body: mainRows,
            styles: { font: 'NotoSansTC', fontSize: 8, cellPadding: 1, valign: 'middle' },
            headStyles: { font: 'NotoSansTC', fontStyle: 'bold', fillColor: [245,210,162], textColor: 60, halign: 'center' },
            alternateRowStyles: { fillColor: [210,210,210] },
            margin: { left: 14, right: 14 },
            columnStyles: creditColumnStyles,
            showHead: 'everyPage'
        });

        const children = creditStatus.child || [];
        for (let i = 0; i < children.length; i++) {
            const group = children[i] || [];
            if (!group || group.length === 0) continue;

            const pageH = doc.internal.pageSize.height;
            let startY = (!justAddedPage && doc.lastAutoTable && typeof doc.lastAutoTable.finalY === 'number') ? doc.lastAutoTable.finalY + 10 : 36;
            if (startY > pageH - 40) {
                doc.addPage();
                justAddedPage = true;
                startY = 36;
            }

            doc.setFontSize(10);
            const cus = group[0] && group[0].CUS_NAME ? group[0].CUS_NAME.trim() + ` (${group[0].CHILD_CUSIDN})` : `關係戶 ${i + 1}`;
            doc.text(cus, 12, startY);

            const tableStart = startY + 7;
            let childRows = group.map(r => ({ CUS_BRANCH: r.CUS_BRANCH || '', CREDIT_ITEM: r.CREDIT_ITEM || '', CONTRACT_AMOUNT: r.CONTRACT_AMOUNT || '', CREDIT_BAL: r.CREDIT_BAL || '', LOAN_TERM: r.LOAN_TERM || '', COLLATERAL: r.COLLATERAL || '', REMARKS: r.REMARKS || '' }));

            // === 新增以下判斷：若無資料，顯示「暫無數據」 ===
            if (childRows.length === 0) {
                childRows = [{
                    CUS_BRANCH: { 
                        content: '暫無數據', 
                        colSpan: 7, 
                        styles: { halign: 'center', textColor: [150, 150, 150], fontSize: 8 } 
                    }
                }];
            }               
            autoTable(doc, { startY: tableStart, columns: mainCols, body: childRows, styles: { font: 'NotoSansTC', fontSize: 8, cellPadding: 1, valign: 'middle' }, headStyles: { font: 'NotoSansTC', fontStyle: 'bold', fillColor: [245,210,162], textColor: 60, halign: 'center' }, 
                alternateRowStyles: { fillColor: [210,210,210] }, margin: { left: 14, right: 14 }, columnStyles: creditColumnStyles, showHead: 'everyPage' });
            justAddedPage = false;
        }

        const allSubTotalRows = (creditStatus.all_subTotal || []).map(r => ({
            CUS_BRANCH: r.CUS_BRANCH || '',
            SUM_CREDIT_ITEM: r.SUM_CREDIT_ITEM || r.CREDIT_ITEM || '總計',
            SUM_CONTRACT_AMOUNT: r.SUM_CONTRACT_AMOUNT || r.CONTRACT_AMOUNT || '',
            SUM_CREDIT_BAL: r.SUM_CREDIT_BAL || r.CREDIT_BAL || '',
            SUM_LOAN_TERM: r.SUM_LOAN_TERM || r.LOAN_TERM || '',
            SUM_COLLATERAL_RATIO: r.SUM_COLLATERAL_RATIO || r.COLLATERAL || '',
            REMARKS: r.REMARKS || ''
        }));

        if (allSubTotalRows.length > 0) {
            const pageHTotal = doc.internal.pageSize.height;
            const prevFinalYTotal = (!justAddedPage && doc.lastAutoTable && typeof doc.lastAutoTable.finalY === 'number') ? doc.lastAutoTable.finalY : 36;
            // 與前一個表格保留一個空白列高度
            let totalStartY = prevFinalYTotal + 16;
            if (totalStartY > pageHTotal - 30) {
                doc.addPage();
                justAddedPage = true;
                totalStartY = 36;
            }

            autoTable(doc, {
                startY: totalStartY,
                body: allSubTotalRows,
                columns: [
                    { header: '分行別', dataKey: 'CUS_BRANCH' },
                    { header: '授信項目', dataKey: 'SUM_CREDIT_ITEM' },
                    { header: '訂約金額', dataKey: 'SUM_CONTRACT_AMOUNT' },
                    { header: '授信餘額', dataKey: 'SUM_CREDIT_BAL' },
                    { header: '貸款期間', dataKey: 'SUM_LOAN_TERM' },
                    { header: '擔保品', dataKey: 'SUM_COLLATERAL_RATIO' },
                    { header: '備註', dataKey: 'REMARKS' }
                ],
                styles: { font: 'NotoSansTC', fontSize: 8, cellPadding: 1, valign: 'middle' },
                margin: { left: 14, right: 14 },
                columnStyles: creditColumnStyles,
                showHead: 'never',
                didParseCell: function(data) {
                    if (data.section === 'body') {
                        data.cell.styles.fillColor = [245,210,162];
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            });
        }

        const pageCount = doc.internal.getNumberOfPages();
        for (let p = 1; p <= pageCount; p++) {
            doc.setPage(p);
            if (watermarkImage) {
                try {
                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();
                    const isPng = (watermarkImage || '').indexOf('data:image/png') === 0 || (watermarkImage || '').indexOf('png') >= 0;
                    const imgFormat = isPng ? 'PNG' : 'JPEG';
                    const imgProps = doc.getImageProperties ? doc.getImageProperties(watermarkImage) : null;
                    const baseTileWidth = Math.min(pageWidth * 0.28, imgProps ? imgProps.width : pageWidth * 0.28);
                    const scale = imgProps ? baseTileWidth / imgProps.width : 1;
                    const tileW = baseTileWidth;
                    const tileH = imgProps ? imgProps.height * scale : baseTileWidth;
                    const gapX = tileW * 0.15;
                    const gapY = tileH * 0.25;
                    drawTiledImageWatermark(doc, watermarkImage, imgFormat, pageWidth, pageHeight, tileW, tileH, gapX, gapY);
                } catch (e) {
                    addWatermark(doc, watermarkText);
                }
            } else {
                addWatermark(doc, watermarkText);
            }

            const pageH = doc.internal.pageSize.height;
            doc.setFontSize(7);
            doc.setTextColor(100);
            doc.text(`第 ${p} 頁　列印時間：${new Date().toLocaleString('zh-TW')}`, 14, pageH - 8);
            doc.setTextColor(0);
        }

        const ts = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_');
        const safeName = companyName.trim().replace(/[\\/:*?"<>|]/g, '_');
        const fileName = `${safeName || fallbackCompanyName}_${ts}.pdf`;
        doc.save(fileName);

        notify({ title: '成功', message: 'PDF 已匯出', type: 'success' });

    } catch (err) {
        console.error('exportCompanyOverviewPdf error', err);
        notify({ title: '錯誤', message: `PDF 匯出失敗：${err.message || err}`, type: 'error' });
    } finally {
        store && store.dispatch && store.dispatch('loadingHandler', false);
    }
}